'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/cn';

export interface TabDef {
  key: string;
  label: ReactNode;
  /** Small mono count shown after the label. */
  badge?: number | string;
  /**
   * Route this tab lives at. When set, the tab renders as a real link so
   * middle-click / Cmd-click / right-click → "Open in new tab" work; plain
   * click still fires `onChange` for instant state updates.
   */
  href?: string;
}

interface TabsProps {
  tabs: TabDef[];
  active: string;
  /** Optional when every tab has a `href` — the links carry the navigation. */
  onChange?: (key: string) => void;
  /** Passed to linked tabs: replace the history entry instead of pushing. */
  linkReplace?: boolean;
  /** Passed to linked tabs: keep the scroll position on navigation. */
  linkNoScroll?: boolean;
  className?: string;
}

export function Tabs({ tabs, active, onChange, linkReplace, linkNoScroll, className }: TabsProps) {
  return (
    // The scrolling row is a child pulled 1px over the nav's border so the
    // active underline still overlaps it without the overflow clipping it.
    <nav className={cn('border-b border-zinc-800', className)}>
      <div className="thin-scroll -mb-px flex gap-1 overflow-x-auto" role="tablist">
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        const tabClass = cn(
          'flex h-9 shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm font-medium whitespace-nowrap transition-colors pointer-coarse:h-11',
          isActive
            ? 'border-accent-bright text-zinc-50'
            : 'border-transparent text-zinc-400 hover:text-zinc-200'
        );
        const inner = (
          <>
            {tab.label}
            {tab.badge !== undefined && tab.badge !== 0 ? (
              <span className="text-xs tabular-nums text-zinc-500">{tab.badge}</span>
            ) : null}
          </>
        );
        return tab.href ? (
          <Link
            key={tab.key}
            href={tab.href}
            replace={linkReplace}
            scroll={!linkNoScroll}
            role="tab"
            aria-selected={isActive}
            onClick={onChange ? () => onChange(tab.key) : undefined}
            className={tabClass}
          >
            {inner}
          </Link>
        ) : (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={onChange ? () => onChange(tab.key) : undefined}
            className={tabClass}
          >
            {inner}
          </button>
        );
      })}
      </div>
    </nav>
  );
}
